import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import DemoBanner from "@/components/DemoBanner";
import { basketCount, subscribeBasket } from "@/lib/programBasket";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Boxes,
  ListChecks,
  FileImage,
  Handshake,
  GraduationCap,
  Palette,
  Workflow,
  Receipt,
  Menu,
  X,
  Compass,
  PackagePlus,
  BarChart3,
  ChevronDown,
  Rocket,
  Brain,
  Sparkles,
  Wrench,
  Lightbulb,
  CreditCard,
  Megaphone,
  MessageSquare,
  FileBadge,
  GitBranch,
  Gauge,
} from "lucide-react";

/**
 * Clinic Portal sidebar — restructured per the June 2026 rename pass.
 * Five primary groups with the order specified by the founder.
 */
const SECTIONS = [
  {
    id: "my-clinic",
    label: "My Clinic",
    icon: Brain,
    items: [
      { to: "/portal", label: "Intelligence Center", icon: LayoutDashboard, id: "home", end: true },
      { to: "/portal/opportunity-scanner", label: "Opportunity Scanner", icon: Compass, id: "opportunity" },
    ],
  },
  {
    id: "consumer-engagement",
    label: "Consumer Engagement",
    icon: Sparkles,
    items: [
      { to: "/portal/screeners", label: "Digital Assessments", icon: ListChecks, id: "screeners" },
      { to: "/portal/marketing", label: "Marketing Assets", icon: FileImage, id: "marketing" },
      { to: "/portal/marketing/campaigns", label: "Marketing Campaigns", icon: Megaphone, id: "campaigns" },
    ],
  },
  {
    id: "consumer-programs",
    label: "Consumer Programs",
    icon: Rocket,
    items: [
      { to: "/portal/launch", label: "Launch Program", icon: Rocket, id: "launch" },
      { to: "/portal/catalog", label: "Services Catalog", icon: Boxes, id: "catalog" },
    ],
  },
  {
    id: "vendors",
    label: "Vendors",
    icon: Handshake,
    items: [
      { to: "/portal/vendors", label: "Vendor Network", icon: Handshake, id: "vendors" },
      { to: "/portal/analytics/vendors", label: "Vendor Performance", icon: BarChart3, id: "vendor-performance" },
      { to: "/portal/invoices", label: "Vendor Invoices", icon: Receipt, id: "invoices" },
    ],
  },
  {
    id: "admin-tools",
    label: "Admin Tools",
    icon: Wrench,
    items: [
      { to: "/portal/analytics", label: "Analytics", icon: BarChart3, id: "analytics",
        match: ["/portal/analytics"], aliasTo: "/portal/analytics/leakage" },
      { to: "/portal/messages", label: "Messaging Center", icon: MessageSquare, id: "messages" },
      { to: "/portal/settings", label: "Clinic Branding", icon: Palette, id: "settings" },
      { to: "/portal/training", label: "Platform Training", icon: GraduationCap, id: "training" },
      { to: "/portal/think-tank", label: "Think Tank", icon: Lightbulb, id: "think-tank" },
      { to: "/portal/billing", label: "Billing & Plans", icon: CreditCard, id: "billing" },
      { to: "/portal/documents", label: "Documents", icon: FileBadge, id: "documents" },
    ],
  },
];

function isItemActive(pathname, item) {
  if (item.end) return pathname === item.to;
  if (item.match) {
    return item.match.some((m) => pathname.startsWith(m));
  }
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

function isSectionActive(pathname, section) {
  return section.items.some((it) => isItemActive(pathname, it));
}

function NavRow({ item, onClick, badge }) {
  const target = item.aliasTo || item.to;
  return (
    <NavLink
      to={target}
      end={item.end}
      data-testid={`portal-nav-${item.id}`}
      onClick={onClick}
      className={({ isActive }) => {
        // For aliased routes, NavLink's isActive is keyed off `target`.
        // For non-aliased: the same `to` is what isActive checks.
        return `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive ? "bg-slate-900 text-white shadow-sm" : "text-slate-700 hover:bg-white hover:text-slate-900"
        }`;
      }}
    >
      <item.icon className="w-4 h-4 shrink-0" />
      <span className="truncate flex-1">{item.label}</span>
      {badge != null && badge > 0 && (
        <span
          data-testid={`portal-nav-${item.id}-badge`}
          className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-bold"
          title="Pending program selections"
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function SectionGroup({ section, pathname, onItemClick, openIds, toggle, itemBadges }) {
  const Icon = section.icon;
  const sectionActive = isSectionActive(pathname, section);
  const isOpen = openIds.has(section.id) || sectionActive;
  // Roll up child badges so the section header shows a count even when
  // collapsed.
  const sectionBadge = section.items.reduce(
    (sum, it) => sum + (itemBadges?.[it.id] || 0),
    0,
  );
  return (
    <div className="space-y-1">
      <button
        type="button"
        data-testid={`portal-nav-section-${section.id}`}
        onClick={() => toggle(section.id)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
          sectionActive
            ? "text-slate-900 bg-white shadow-sm border border-slate-200"
            : "text-slate-600 hover:text-slate-900"
        }`}
      >
        <Icon className="w-3.5 h-3.5 shrink-0 text-blue-600" />
        <span className="truncate flex-1 text-left">{section.label}</span>
        {sectionBadge > 0 && (
          <span
            data-testid={`portal-nav-section-${section.id}-badge`}
            className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-500 text-white text-[10px] font-bold"
            title="Pending program selections"
          >
            {sectionBadge}
          </span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform text-slate-400 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="ml-1 pl-3 border-l-2 border-slate-100 space-y-0.5">
          {section.items.map((it) => (
            <NavRow
              key={it.to}
              item={it}
              onClick={onItemClick}
              badge={itemBadges?.[it.id]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PortalShell() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // Sections marked open by user (in addition to whichever section contains
  // the active route, which is always treated as open).
  const [openIds, setOpenIds] = useState(() => new Set(SECTIONS.map((s) => s.id)));
  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const close = () => setOpen(false);

  // Live basket count — surfaces a blue pill on the Services Catalog row
  // (and on its parent Operations section header) so users keep visibility
  // of pending program selections from any page.
  const [basketSize, setBasketSize] = useState(basketCount());
  useEffect(() => subscribeBasket((items) => setBasketSize(items.length)), []);

  // Live activity counts — unread messages + open workflows. Polled every
  // 60s so the sidebar acts as a real activity center.
  const [counts, setCounts] = useState({ messages_unread: 0, workflows_open: 0 });
  useEffect(() => {
    let alive = true;
    const fetchCounts = () => {
      api
        .get("/clinic/sidebar-counts")
        .then((r) => {
          if (alive) setCounts(r.data || {});
        })
        .catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const itemBadges = {
    catalog: basketSize,
    messages: counts.messages_unread,
    workflows: counts.workflows_open,
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)]" data-testid="portal-shell">
      <div className="lg:hidden fixed top-16 left-0 right-0 z-30 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600">Clinic portal</div>
        <button
          type="button"
          data-testid="portal-shell-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
        >
          {open ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />} Menu
        </button>
      </div>

      <aside
        data-testid="portal-shell-sidebar"
        className={`${
          open ? "block" : "hidden"
        } lg:block fixed top-16 left-0 bottom-0 w-64 bg-slate-50 border-r border-slate-200 z-20 overflow-y-auto pt-6 pb-10 px-4`}
      >
        <div className="px-2 mb-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
          Clinic Portal
        </div>
        <nav className="space-y-3">
          {SECTIONS.map((s) => (
            <SectionGroup
              key={s.id}
              section={s}
              pathname={loc.pathname}
              onItemClick={close}
              openIds={openIds}
              toggle={toggle}
              itemBadges={itemBadges}
            />
          ))}
        </nav>
      </aside>

      <div className="flex-1 lg:pl-64">
        <DemoBanner />
        <Outlet />
      </div>
    </div>
  );
}
